import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Card, Row, Col, Badge, ListGroup, Table } from "react-bootstrap";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { fetchShiftDetails, fetchShiftWithDeliveries } from "./LivreursShiftsService";

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function ShiftDetailsModal({ show, onHide, shiftId }) {
  const [loading, setLoading] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [deliveriesData, setDeliveriesData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (show && shiftId) {
      setIsInitialLoad(true);
      fetchDetails(true);

      // Auto-refresh every 15 seconds when modal is open
      const interval = setInterval(() => {
        fetchDetails(false);
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [show, shiftId]);

  const fetchDetails = async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      // Fetch both GPS history and deliveries data in parallel
      const [gpsData, delivData] = await Promise.all([
        fetchShiftDetails(shiftId),
        fetchShiftWithDeliveries(shiftId)
      ]);
      setShiftData(gpsData);
      setDeliveriesData(delivData?.shift);
      setLastUpdate(new Date());
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Error fetching shift details:", error);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!endTime) return "En cours";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const openInGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // Calculate map center and bounds
  const getMapCenterAndBounds = (positions) => {
    if (!positions || positions.length === 0) return null;

    const latitudes = positions.map((p) => p.latitude);
    const longitudes = positions.map((p) => p.longitude);

    const center = {
      lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
      lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
    };

    const bounds = [
      [Math.min(...latitudes), Math.min(...longitudes)],
      [Math.max(...latitudes), Math.max(...longitudes)],
    ];

    return { center, bounds };
  };

  const mapData = shiftData?.positions ? getMapCenterAndBounds(shiftData.positions) : null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Détails du Shift
          {lastUpdate && (
            <small className="text-muted ms-3" style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
              Dernière mise à jour: {format(lastUpdate, "HH:mm:ss", { locale: fr })}
            </small>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </div>
        ) : shiftData ? (
          <>
            {/* Shift Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Informations du Shift</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Livreur:</strong> {shiftData.shift.livreur_name}
                    </p>
                    <p>
                      <strong>Début:</strong>{" "}
                      {format(new Date(shiftData.shift.start_time), "dd/MM/yyyy à HH:mm", {
                        locale: fr,
                      })}
                    </p>
                    <p>
                      <strong>Fin:</strong>{" "}
                      {shiftData.shift.end_time ? (
                        format(new Date(shiftData.shift.end_time), "dd/MM/yyyy à HH:mm", {
                          locale: fr,
                        })
                      ) : (
                        <Badge bg="warning">En cours</Badge>
                      )}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Durée:</strong>{" "}
                      {formatDuration(shiftData.shift.start_time, shiftData.shift.end_time)}
                    </p>
                    <p>
                      <strong>Distance parcourue:</strong>{" "}
                      {shiftData.shift.total_distance_km
                        ? `${shiftData.shift.total_distance_km} km`
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Nombre de livraisons:</strong> {shiftData.shift.total_deliveries || 0}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Deliveries Statistics */}
            {deliveriesData && deliveriesData.deliveries && deliveriesData.deliveries.length > 0 ? (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Statistiques des Livraisons</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Card className="text-center mb-3 mb-md-0">
                        <Card.Body>
                          <h6 className="text-muted mb-2">Livraisons</h6>
                          <h3 className="mb-0">{deliveriesData.completed_deliveries_count}</h3>
                          <small className="text-muted">
                            sur {deliveriesData.deliveries_count} total
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3 mb-md-0">
                        <Card.Body>
                          <h6 className="text-muted mb-2">Temps moyen</h6>
                          <h3 className="mb-0">{deliveriesData.avg_delivery_time_formatted || "N/A"}</h3>
                          <small className="text-muted">
                            {deliveriesData.avg_delivery_time_minutes?.toFixed(1)} min
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3 mb-md-0">
                        <Card.Body>
                          <h6 className="text-muted mb-2">Distance totale</h6>
                          <h3 className="mb-0">{deliveriesData.total_delivery_distance_km?.toFixed(1) || "N/A"} km</h3>
                          <small className="text-muted">
                            Moy: {deliveriesData.avg_delivery_distance_km?.toFixed(2)} km
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center mb-3 mb-md-0">
                        <Card.Body>
                          <h6 className="text-muted mb-2">Vitesse</h6>
                          <h3 className="mb-0">{deliveriesData.avg_speed_kmh?.toFixed(1) || "N/A"} km/h</h3>
                          <small className="text-muted">
                            Max: {deliveriesData.max_speed_kmh?.toFixed(1)} km/h
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <Card className="mb-4 bg-light">
                <Card.Body className="text-center py-4">
                  <p className="text-muted mb-1">
                    <i className="bi bi-info-circle me-2"></i>
                    Aucun détail de livraison disponible pour ce shift
                  </p>
                  <small className="text-muted">
                    Les statistiques détaillées par livraison sont disponibles uniquement pour les shifts récents
                  </small>
                </Card.Body>
              </Card>
            )}

            {/* Map with GPS Trajectory */}
            {shiftData.positions && shiftData.positions.length > 0 && mapData ? (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    Trajet GPS ({shiftData.total_positions} positions enregistrées)
                  </h5>
                </Card.Header>
                <Card.Body>
                  <MapContainer
                    center={[mapData.center.lat, mapData.center.lng]}
                    zoom={13}
                    style={{ height: "400px", width: "100%" }}
                    bounds={mapData.bounds}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />

                    {/* Draw polyline for trajectory */}
                    <Polyline
                      positions={shiftData.positions.map((p) => [p.latitude, p.longitude])}
                      color="blue"
                      weight={3}
                      opacity={0.7}
                    />

                    {/* Start marker */}
                    {shiftData.positions.length > 0 && (
                      <Marker
                        position={[
                          shiftData.positions[0].latitude,
                          shiftData.positions[0].longitude,
                        ]}
                        icon={
                          new L.Icon({
                            iconUrl:
                              "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
                            shadowUrl:
                              "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
                            iconSize: [25, 41],
                            iconAnchor: [12, 41],
                            popupAnchor: [1, -34],
                            shadowSize: [41, 41],
                          })
                        }
                      >
                        <Popup>
                          <strong>Début du shift</strong>
                          <br />
                          {format(new Date(shiftData.positions[0].recorded_at), "HH:mm:ss", {
                            locale: fr,
                          })}
                          <br />
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() =>
                              openInGoogleMaps(
                                shiftData.positions[0].latitude,
                                shiftData.positions[0].longitude
                              )
                            }
                          >
                            Ouvrir dans Google Maps
                          </Button>
                        </Popup>
                      </Marker>
                    )}

                    {/* End marker */}
                    {shiftData.positions.length > 1 && (
                      <Marker
                        position={[
                          shiftData.positions[shiftData.positions.length - 1].latitude,
                          shiftData.positions[shiftData.positions.length - 1].longitude,
                        ]}
                        icon={
                          new L.Icon({
                            iconUrl:
                              "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
                            shadowUrl:
                              "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
                            iconSize: [25, 41],
                            iconAnchor: [12, 41],
                            popupAnchor: [1, -34],
                            shadowSize: [41, 41],
                          })
                        }
                      >
                        <Popup>
                          <strong>
                            {shiftData.shift.end_time ? "Fin du shift" : "Position actuelle"}
                          </strong>
                          <br />
                          {format(
                            new Date(
                              shiftData.positions[shiftData.positions.length - 1].recorded_at
                            ),
                            "HH:mm:ss",
                            { locale: fr }
                          )}
                          <br />
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() =>
                              openInGoogleMaps(
                                shiftData.positions[shiftData.positions.length - 1].latitude,
                                shiftData.positions[shiftData.positions.length - 1].longitude
                              )
                            }
                          >
                            Ouvrir dans Google Maps
                          </Button>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </Card.Body>
              </Card>
            ) : (
              <Card className="mb-4">
                <Card.Body className="text-center text-muted">
                  Aucune position GPS enregistrée pour ce shift
                </Card.Body>
              </Card>
            )}

            {/* Deliveries Details Table */}
            {deliveriesData && deliveriesData.deliveries && deliveriesData.deliveries.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Détail des Livraisons ({deliveriesData.deliveries.length})</h5>
                </Card.Header>
                <Card.Body>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th>Commande</th>
                          <th>Client</th>
                          <th>Adresse</th>
                          <th>Début</th>
                          <th>Fin</th>
                          <th>Durée</th>
                          <th>Distance</th>
                          <th>Vitesse moy.</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveriesData.deliveries.map((delivery) => (
                          <tr key={delivery.id}>
                            <td>
                              <strong>{delivery.commande?.reference || `#${delivery.commande_id}`}</strong>
                            </td>
                            <td>{delivery.commande?.user_name || "N/A"}</td>
                            <td>
                              <small>{delivery.commande?.adresse || "N/A"}</small>
                            </td>
                            <td>
                              <small>
                                {delivery.started_at
                                  ? format(new Date(delivery.started_at), "HH:mm", { locale: fr })
                                  : "N/A"}
                              </small>
                            </td>
                            <td>
                              <small>
                                {delivery.completed_at
                                  ? format(new Date(delivery.completed_at), "HH:mm", { locale: fr })
                                  : "-"}
                              </small>
                            </td>
                            <td>
                              <Badge bg={delivery.is_completed ? "success" : "warning"}>
                                {delivery.delivery_duration_formatted || "En cours"}
                              </Badge>
                            </td>
                            <td>
                              <small>{delivery.distance_km?.toFixed(2) || "N/A"} km</small>
                            </td>
                            <td>
                              <small>{delivery.avg_speed_kmh?.toFixed(1) || "N/A"} km/h</small>
                            </td>
                            <td>
                              {delivery.is_completed ? (
                                <Badge bg="success">Terminée</Badge>
                              ) : delivery.is_in_progress ? (
                                <Badge bg="warning">En cours</Badge>
                              ) : (
                                <Badge bg="secondary">-</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Recent Positions List */}
            {shiftData.positions && shiftData.positions.length > 0 && (
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Dernières positions enregistrées</h5>
                </Card.Header>
                <Card.Body>
                  <ListGroup style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {shiftData.positions
                      .slice()
                      .reverse()
                      .slice(0, 20)
                      .map((position, index) => (
                        <ListGroup.Item key={index}>
                          <Row>
                            <Col md={3}>
                              <small className="text-muted">
                                {format(new Date(position.recorded_at), "dd/MM/yyyy HH:mm:ss", {
                                  locale: fr,
                                })}
                              </small>
                            </Col>
                            <Col md={3}>
                              <small>
                                {parseFloat(position.latitude).toFixed(6)}, {parseFloat(position.longitude).toFixed(6)}
                              </small>
                            </Col>
                            <Col md={2}>
                              {position.speed && <small>Vitesse: {position.speed} km/h</small>}
                            </Col>
                            <Col md={2}>
                              <Badge bg={position.tracking_mode === "delivery" ? "primary" : "secondary"}>
                                {position.tracking_mode === "delivery" ? "Livraison" : "Service"}
                              </Badge>
                            </Col>
                            <Col md={2}>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openInGoogleMaps(position.latitude, position.longitude)}
                              >
                                Voir
                              </Button>
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">Aucune donnée disponible</p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ShiftDetailsModal;
